import { useEffect, useMemo } from "react";
import { Link, useParams } from "wouter";
import { Award, CheckCircle2, ClipboardCheck, Cpu, FileCheck2, FileSearch, GraduationCap, ListChecks, Shield, ShieldCheck, Stethoscope, UserPlus, XCircle } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import {
  useAuditLog,
  useCandidate,
  useDictamenForExpediente,
  useEnrollmentsForCandidate,
  useExpediente,
  useGroup,
  useLmsAvancePct,
  useLmsProgress,
  useLmsRoute,
  usePrechecklistForExpediente,
  usePreopRuns,
  useStandard,
  useUsers,
} from "@/store/hooks";
import { useDemoStore } from "@/store/useDemoStore";
import { downloadCSV, downloadJSON } from "@/utils/download";

type TimelineEvent = {
  id: string;
  ts: string;
  titulo: string;
  detalle: string;
  actor?: string;
  tono: "navy" | "teal" | "green" | "amber" | "red";
  icon: React.ComponentType<{ size?: number; color?: string }>;
};

export default function AuditorExpediente() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("auditor"), [setRole]);

  const params = useParams<{ expedienteId: string }>();
  const exp = useExpediente(params.expedienteId);
  const cand = useCandidate(exp?.candidatoId);
  const standard = useStandard(exp?.estandar);
  const group = useGroup(exp?.grupoId);
  const dictamen = useDictamenForExpediente(exp?.id);
  const prechecklist = usePrechecklistForExpediente(exp?.id);
  const enrollments = useEnrollmentsForCandidate(exp?.candidatoId);
  const auditLog = useAuditLog();
  const users = useUsers();

  const events = useMemo<TimelineEvent[]>(() => {
    if (!exp) return [];
    const evs: TimelineEvent[] = [];
    // Inscripción — derivada del auditLog si existe, si no del grupo/enrollments como fallback
    const enrollLog = auditLog.find(
      (ev) => ev.entidad === "expediente" && ev.entidadId === exp.id && /inscrib/i.test(ev.accion),
    );
    if (enrollLog) {
      evs.push({
        id: "inscripcion", ts: enrollLog.ts,
        titulo: "Inscripción al estándar",
        detalle: `${exp.estandar}${group ? ` · ${group.nombre}` : ""}`,
        actor: users[enrollLog.actor]?.nombre ?? enrollLog.actor,
        tono: "navy", icon: UserPlus,
      });
    } else if (enrollments?.includes(exp.estandar) && group?.inicio) {
      evs.push({
        id: "inscripcion", ts: group.inicio,
        titulo: "Inscripción al estándar",
        detalle: `${exp.estandar} · ${group.nombre}`,
        tono: "navy", icon: UserPlus,
      });
    }
    if (exp.diagnostico.aplicado && exp.diagnostico.fecha) {
      evs.push({
        id: "diag", ts: exp.diagnostico.fecha,
        titulo: "Diagnóstico aplicado",
        detalle: `Puntaje ${exp.diagnostico.puntaje ?? "—"}`,
        tono: "teal", icon: Stethoscope,
      });
    }
    if (exp.examenTeorico.aplicado && exp.examenTeorico.fecha) {
      evs.push({
        id: "teorico", ts: exp.examenTeorico.fecha,
        titulo: "Examen teórico",
        detalle: `Puntaje ${exp.examenTeorico.puntaje ?? "—"} — ${exp.examenTeorico.aprobado ? "aprobado" : "no aprobado"}`,
        tono: exp.examenTeorico.aprobado ? "green" : "red",
        icon: GraduationCap,
      });
    }
    if (prechecklist?.completedAt) {
      const total = prechecklist.entries.length;
      const checked = prechecklist.entries.filter((e) => e.checked).length;
      evs.push({
        id: "prechecklist", ts: prechecklist.completedAt,
        titulo: "Lista preoperativa firmada",
        detalle: `${checked}/${total} elementos verificados`,
        actor: prechecklist.evaluadorId ? (users[prechecklist.evaluadorId]?.nombre ?? prechecklist.evaluadorId) : undefined,
        tono: checked === total ? "green" : "amber",
        icon: ListChecks,
      });
    }
    if (exp.evaluacionPractica.aplicada && exp.evaluacionPractica.fecha !== "—") {
      const pct = exp.evaluacionPractica.criteriosTotales > 0
        ? Math.round((exp.evaluacionPractica.criteriosCompetente / exp.evaluacionPractica.criteriosTotales) * 100)
        : 0;
      evs.push({
        id: "practica", ts: exp.evaluacionPractica.fecha,
        titulo: "Evaluación práctica enviada",
        detalle: `${exp.evaluacionPractica.criteriosCompetente}/${exp.evaluacionPractica.criteriosTotales} criterios competentes (${pct}%)`,
        tono: pct >= 90 ? "green" : pct >= 80 ? "amber" : "red",
        icon: ClipboardCheck,
      });
    }
    if (dictamen) {
      evs.push({
        id: "dictamen", ts: dictamen.fecha,
        titulo: `Dictamen: ${dictamen.resultado.toUpperCase()}`,
        detalle: dictamen.fundamento.length > 140 ? dictamen.fundamento.slice(0, 140) + "…" : dictamen.fundamento,
        actor: users[dictamen.dictaminadorId]?.nombre ?? dictamen.dictaminadorId,
        tono: dictamen.resultado === "competente" ? "green" : "red",
        icon: FileCheck2,
      });
    }
    if (exp.certificado.emitido && exp.certificado.fechaEmision) {
      evs.push({
        id: "cert", ts: exp.certificado.fechaEmision,
        titulo: `Certificado emitido (${exp.certificado.folio ?? "—"})`,
        detalle: `Vigencia ${standard?.vigencia ?? "—"} a partir de ${exp.certificado.fechaEmision}`,
        tono: "green", icon: Award,
      });
    }
    if (dictamen?.revocacion) {
      evs.push({
        id: "revoc", ts: dictamen.revocacion.fecha,
        titulo: "Certificado revocado",
        detalle: dictamen.revocacion.motivo,
        actor: users[dictamen.revocacion.actor]?.nombre ?? dictamen.revocacion.actor,
        tono: "red", icon: XCircle,
      });
    }
    // Audit log events for this expediente (other than ones we already represent)
    for (const ev of auditLog) {
      if (ev.entidad !== "expediente" || ev.entidadId !== exp.id) continue;
      if (/dictamen|evaluación práctica|inscrib|preoperativa/i.test(ev.accion)) continue;
      evs.push({
        id: ev.id, ts: ev.ts,
        titulo: ev.accion.charAt(0).toUpperCase() + ev.accion.slice(1),
        detalle: ev.entidad,
        actor: users[ev.actor]?.nombre ?? ev.actor,
        tono: "navy", icon: Shield,
      });
    }
    return evs.sort((a, b) => a.ts.localeCompare(b.ts));
  }, [exp, dictamen, auditLog, users, standard, group, prechecklist, enrollments]);

  if (!exp) {
    return (
      <AppLayout pageTitle="Expediente no encontrado">
        <Card>
          <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>
            El expediente <strong>{params.expedienteId}</strong> no existe.{" "}
            <Link href="/auditor" data-testid="link-back-auditor" style={{ color: "var(--cc-teal)" }}>Volver al dashboard</Link>
          </div>
        </Card>
      </AppLayout>
    );
  }

  const handleExport = (kind: "csv" | "json") => {
    const fname = `auditoria-${exp.id}`;
    if (kind === "csv") {
      downloadCSV(`${fname}-timeline.csv`, events.map((e) => ({
        fecha: e.ts, titulo: e.titulo, detalle: e.detalle, actor: e.actor ?? "",
      })));
    } else {
      downloadJSON(`${fname}.json`, {
        expediente: exp, candidato: cand, grupo: group, estandar: standard, dictamen, timeline: events,
      });
    }
  };

  return (
    <AppLayout breadcrumb={["Auditor", `Expediente ${exp.id.toUpperCase()}`]}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>Auditoría — solo lectura</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>{cand?.nombre ?? exp.candidatoId}</h1>
          <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>{group?.nombre ?? exp.grupoId} · {exp.id.toUpperCase()} · {exp.estandar}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Badge tone={exp.dictamen === "competente" ? "green" : exp.dictamen === "todavia-no-competente" ? "red" : "gray"}>{exp.dictamen}</Badge>
          <PrimaryButton variant="outline" testId="button-export-csv" onClick={() => handleExport("csv")}>CSV</PrimaryButton>
          <PrimaryButton variant="outline" testId="button-export-json" onClick={() => handleExport("json")}>JSON</PrimaryButton>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <Card>
          <SectionTitle>Línea de tiempo del expediente</SectionTitle>
          {events.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }} data-testid="text-no-events">Sin eventos registrados todavía para este expediente.</div>
          ) : (
            <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }} data-testid="list-timeline">
              {events.map((e) => (
                <li key={e.id} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: toneBg(e.tono), color: toneFg(e.tono), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <e.icon size={16} />
                  </div>
                  <div style={{ flex: 1, borderBottom: "1px solid var(--cc-gray-100)", paddingBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{e.titulo}</span>
                      <span style={{ fontSize: 11, color: "var(--cc-gray-500)", fontFamily: "var(--app-font-mono)" }}>{e.ts.slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--cc-gray-700)", marginTop: 2 }}>{e.detalle}</div>
                    {e.actor && <div style={{ fontSize: 11, color: "var(--cc-gray-500)", marginTop: 2 }}>por {e.actor}</div>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <ShieldCheck size={15} color="var(--cc-green)" />
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-green)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Estado del expediente</span>
            </div>
            <Row label="Diagnóstico" ok={exp.diagnostico.aplicado} />
            <Row label="Examen teórico" ok={exp.examenTeorico.aprobado} />
            <Row label="Evaluación práctica" ok={exp.evaluacionPractica.aplicada} />
            <Row label="Evidencias completas" ok={exp.evidencias.entregadas >= exp.evidencias.requeridas} />
            <Row label="Dictamen emitido" ok={exp.dictamen !== "pendiente"} />
            <Row label="Certificado vigente" ok={exp.certificado.emitido} />
          </Card>

          <AuditorLmsCard expedienteId={exp.id} codigo={exp.estandar} />

          {exp.alertasIA.length > 0 && (
            <Card style={{ background: "var(--cc-teal-bg)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <Cpu size={15} color="var(--cc-teal)" />
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-teal)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Hallazgos IA</span>
              </div>
              <ul style={{ paddingLeft: 16, fontSize: 12, color: "var(--cc-gray-700)", lineHeight: 1.6 }}>
                {exp.alertasIA.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </Card>
          )}

          <Link href={`/dictaminador/expediente/${exp.id}`} data-testid="link-go-dictaminador">
            <PrimaryButton variant="outline"><FileSearch size={13} /> Ver con el dictaminador</PrimaryButton>
          </Link>
        </div>
      </div>
    </AppLayout>
  );
}

function toneBg(t: TimelineEvent["tono"]): string {
  return t === "green" ? "var(--cc-green-bg)" : t === "red" ? "var(--cc-red-bg)" : t === "amber" ? "var(--cc-amber-bg)" : t === "teal" ? "var(--cc-teal-bg)" : "var(--cc-gray-100)";
}
function toneFg(t: TimelineEvent["tono"]): string {
  return t === "green" ? "var(--cc-green)" : t === "red" ? "var(--cc-red)" : t === "amber" ? "var(--cc-amber)" : t === "teal" ? "var(--cc-teal)" : "var(--cc-navy)";
}

function AuditorLmsCard({ expedienteId, codigo }: { expedienteId: string; codigo: string }) {
  const route = useLmsRoute(codigo);
  const progress = useLmsProgress(expedienteId);
  const pct = useLmsAvancePct(expedienteId, codigo);
  const runs = usePreopRuns(expedienteId);
  if (!route) return null;
  const ready = pct >= 80;
  const completed = route.modules
    .filter((m) => progress.completedModuleIds.includes(m.id))
    .map((m) => {
      const atts = progress.attemptsByModule[m.id] ?? [];
      return { mod: m, ts: atts[atts.length - 1]?.ts };
    });
  return (
    <Card testId="auditor-lms-card">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <GraduationCap size={15} color="var(--cc-teal)" />
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-teal)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Formación</span>
      </div>
      <div style={{ fontSize: 13, color: "var(--cc-navy)", fontWeight: 700 }}>
        {progress.completedModuleIds.length}/{route.modules.length} módulos · {pct}%
      </div>
      <div style={{ background: "var(--cc-gray-100)", borderRadius: 999, height: 6, marginTop: 6, overflow: "hidden" }}>
        <div style={{ background: ready ? "var(--cc-green)" : "var(--cc-teal)", width: `${pct}%`, height: "100%" }} />
      </div>
      {completed.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--cc-gray-500)", textTransform: "uppercase", marginBottom: 3 }}>Módulos cursados</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {completed.map(({ mod: m, ts }) => (
              <div key={m.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--cc-gray-700)" }}>
                <span>✓ {m.titulo.length > 30 ? m.titulo.slice(0, 30) + "…" : m.titulo}</span>
                <span style={{ color: "var(--cc-gray-500)" }}>{ts ? new Date(ts).toLocaleDateString("es-MX") : "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {runs.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--cc-gray-500)", textTransform: "uppercase", marginBottom: 3 }}>Simuladores preoperativos</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {[...runs].reverse().map((r) => (
              <div key={r.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--cc-gray-700)" }}>
                <span>{new Date(r.fecha).toLocaleDateString("es-MX")} · {r.completas}/{r.total}{r.criticasFaltantes > 0 ? ` · ${r.criticasFaltantes}cr` : ""}</span>
                <span style={{ color: r.aprobado ? "var(--cc-green)" : "var(--cc-amber)", fontWeight: 700 }}>{r.aprobado ? "✓" : "✗"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function Row({ label, ok }: { label: string; ok?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--cc-gray-100)" }}>
      <span style={{ fontSize: 12, color: "var(--cc-gray-700)" }}>{label}</span>
      {ok ? <CheckCircle2 size={16} color="var(--cc-green)" /> : <span style={{ fontSize: 12, color: "var(--cc-gray-400)" }}>—</span>}
    </div>
  );
}
